import React, { useEffect, useState } from 'react'
import { App, Button, Spin, Table, Typography } from 'antd'
import { CheckCircleTwoTone, CloseCircleTwoTone } from '@ant-design/icons'
import IntlMessages from '../../../../util/IntlMessages'
import { useManualQuery } from 'graphql-hooks'
import { getUsersFiltered } from '../../../../graphql/custom-queries'
import { IGetUsersFiltered, IUserFilteredInfo } from '../../../../types/users'
import { getAuthTokens } from '@vacationtracker/shared/services/auth'
import axios from 'axios'
import { useIntl } from 'react-intl'
import logger from '@vacationtracker/shared/functions/logger'
import { v4 as uuidv4 } from 'uuid'
import { UserAvatar } from '@vacationtracker/shared/components/user-avatar'
import { ADPFlag } from '../../integrated-flag'

const { Title, Paragraph } = Typography

const VT_USERS_LIMIT = 1000
const ADP_USERS_LIMIT = 1500

interface AdpEmployee {
  associateOID: string
  name: string
  emails: string[]
}

// Per-session ADP page cache to avoid duplicate requests per skip
const adpPageCache: Map<number, AdpEmployee[]> = new Map()
const adpSkipFetched: Set<number> = new Set()

interface MatchUsersStepProps {
  onConfirm: (matched: { vtId: string; adpId: string }[]) => void
}

export const MatchUsersStep: React.FC<MatchUsersStepProps> = ({ onConfirm }) => {
  const { notification } = App.useApp()
  const { formatMessage } = useIntl()
  const [getUsersQuery] = useManualQuery<IGetUsersFiltered, { status: string; limit: number; locationIds: string[]; teamIds: string[]; labelIds: string[]; nextToken?: string }>(
    getUsersFiltered
  )
  const [loading, setLoading] = useState(true)
  const [vtUsers, setVtUsers] = useState<IUserFilteredInfo[]>([])
  const [adpUsers, setAdpUsers] = useState<AdpEmployee[]>([])
  const [pageSize, setPageSize] = useState(10)

  useEffect(() => {
    void (async () => {
      setLoading(true)
      await Promise.allSettled([getVtUsers(), getAdpUsers()])
      setLoading(false)
    })()
  }, [])

  const getVtUsers = async () => {
    const collected: IUserFilteredInfo[] = []
    let attempts = 0
    let nextToken: string | undefined = undefined
    do {
      const response = await getUsersQuery({
        variables: { status: 'ACTIVE', limit: VT_USERS_LIMIT, locationIds: [], teamIds: [], labelIds: [], nextToken },
      })
      const pageUsers = (response?.data?.getUsersFiltered?.users ?? []) as IUserFilteredInfo[]
      nextToken = response?.data?.getUsersFiltered?.nextToken
      collected.push(...pageUsers)
      attempts += 1
    } while (attempts < 3 && nextToken)
    setVtUsers(collected)
  }

  const getAdpUsers = async () => {
    try {
      const tokens = await getAuthTokens()
      const collected: AdpEmployee[] = []
      let attempts = 0
      let skip = 0

      while (attempts < 3) {
        let page = adpPageCache.get(skip)
        if (!page && !adpSkipFetched.has(skip)) {
          const response = (await axios.get(`${process.env.REACT_APP_API_URL}/integrations/adp/users`, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${tokens?.idToken}`,
            },
            params: { skip },
          })) as { data: AdpEmployee[] }
          page = response.data || []
          adpPageCache.set(skip, page)
          adpSkipFetched.add(skip)
        }

        if (page && page.length > 0) {
          collected.push(...page)
        }

        if (!page || page.length < ADP_USERS_LIMIT) break

        attempts += 1
        skip += ADP_USERS_LIMIT
      }

      setAdpUsers(collected)
    } catch (error) {
      const id = uuidv4()
      notification.error({
        message: formatMessage({ id: 'notifications.error' }, { correlationId: id }),
        description: formatMessage({ id: 'onboarding.adp.usersStep.failed' }),
        duration: 100,
      })
      logger.error(id, error)
    }
  }

  const adpByEmail = new Map<string, AdpEmployee>()
  adpUsers.forEach((emp) => {
    ;(emp.emails || []).forEach((email) => {
      if (email) adpByEmail.set(email.toLowerCase(), emp)
    })
  })

  const rows = vtUsers.map((u) => {
    const adpIntegratedFlag = (u as unknown as { adpIntegrated?: boolean }).adpIntegrated
    const email = (u.email || '').toLowerCase()
    const adp = email ? adpByEmail.get(email) : undefined
    const isMatched = Boolean(adp)
    return {
      vtId: u.id,
      vtName: u.name,
      vtEmail: u.email,
      vtImageUrl: u.imageUrl,
      adpIntegrated: Boolean(adpIntegratedFlag),
      isMatched,
      adpEmails: adp?.emails || [],
      adpName: adp?.name || '',
      adpAssocId: adp?.associateOID || '',
    }
  })

  const matchedPairs = rows.filter((r) => r.isMatched && r.adpAssocId).map((r) => ({ vtId: r.vtId, adpId: r.adpAssocId as string }))
  const totalMatched = matchedPairs.length

  if (loading) {
    return (
      <div className="users-step-loading-container">
        <Spin size="large" className="users-step-spinner" />
        <div className="users-step-loading-text">
          <IntlMessages id="onboarding.adp.usersStep.loading" />
        </div>
      </div>
    )
  }

  return (
    <div className="users-step-container" style={{ maxWidth: 'none' }}>
      <Title level={2} className="users-step-title">
        <IntlMessages id="onboarding.adpConnector.match.title" />
      </Title>
      <Paragraph className="users-step-description">
        <IntlMessages id="onboarding.adpConnector.match.description" />
      </Paragraph>

      <Table
        dataSource={rows}
        pagination={{
          pageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: (_page, size) => setPageSize(size),
        }}
        rowKey="vtId"
        tableLayout="fixed"
        locale={{
          emptyText: <IntlMessages id="app.importLeaves.validationStep.noRowsMessage" />,
        }}
        columns={[
          {
            title: <IntlMessages id="onboarding.adpConnector.match.columns.vtName" />,
            dataIndex: 'vtName',
            key: 'vtName',
            width: 220,
            render: (_name: string, r: { vtId: string; vtName: string; vtImageUrl?: string; adpIntegrated: boolean }) => (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <UserAvatar id={r.vtId} name={r.vtName} shape="circle" avatarSize="small" avatar={r.vtImageUrl} />
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.vtName}</span>
                {r.adpIntegrated && <ADPFlag />}
              </div>
            ),
          },
          {
            title: <IntlMessages id="onboarding.adpConnector.match.columns.vtEmail" />,
            dataIndex: 'vtEmail',
            key: 'vtEmail',
            width: 220,
            ellipsis: true as unknown as boolean,
          },
          {
            title: <IntlMessages id="onboarding.adpConnector.match.columns.matched" />,
            dataIndex: 'isMatched',
            key: 'isMatched',
            width: 80,
            render: (value: boolean) => (value ? <CheckCircleTwoTone twoToneColor="#52c41a" /> : <CloseCircleTwoTone twoToneColor="#ff4d4f" />),
          },
          {
            title: <IntlMessages id="onboarding.adpConnector.match.columns.adpEmails" />,
            dataIndex: 'adpEmails',
            key: 'adpEmails',
            width: 240,
            ellipsis: true as unknown as boolean,
            render: (emails: string[]) => {
              if (!emails || emails.length === 0) return '-'
              const text = emails.join(', ')
              return text
            },
          },
          {
            title: <IntlMessages id="onboarding.adpConnector.match.columns.adpName" />,
            dataIndex: 'adpName',
            key: 'adpName',
            width: 180,
            ellipsis: true as unknown as boolean,
          },
          {
            title: <IntlMessages id="onboarding.adpConnector.match.columns.adpAssocId" />,
            dataIndex: 'adpAssocId',
            key: 'adpAssocId',
            width: 200,
            ellipsis: true as unknown as boolean,
          },
        ]}
      />

      <div className="users-step-actions">
        <Button type="primary" size="large" onClick={() => onConfirm(matchedPairs)} disabled={matchedPairs.length < 1} className="users-step-import-btn">
          <IntlMessages id="onboarding.adpConnector.match.confirmButton" values={{ total: totalMatched }} />
        </Button>
      </div>
    </div>
  )
}
